
import { GoogleGenAI, Type } from "@google/genai";
import { GeneratedContent, FileData } from "../types";

export const processContent = async (
  input: string | FileData,
  mode: 'text' | 'file'
): Promise<GeneratedContent> => {
  // process.env.API_KEY is injected by Vite during the Netlify build process
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY || '' });
  
  const systemInstruction = `
    You are an expert content strategist for speakers, teachers, and churches. 
    Your task is to take a message (sermon, talk, or lesson) and transform it into shareable social media content.
    Provide:
    1. Exactly 5 key points summarizing the message.
    2. Exactly 5 punchy, short quotes suitable for social media graphics.
    3. Exactly 3 general social media captions that invite engagement.
    
    Output the result in valid JSON format.
  `;

  const responseSchema = {
    type: Type.OBJECT,
    properties: {
      keyPoints: {
        type: Type.ARRAY,
        items: { type: Type.STRING },
        description: "5 key takeaways from the message",
      },
      quotes: {
        type: Type.ARRAY,
        items: { type: Type.STRING },
        description: "5 short impactful quotes",
      },
      captions: {
        type: Type.ARRAY,
        items: { type: Type.STRING },
        description: "3 general social media captions",
      },
    },
    required: ["keyPoints", "quotes", "captions"],
  };

  const model = "gemini-3-flash-preview";
  let contents: any;

  if (mode === 'text' && typeof input === 'string') {
    contents = {
      parts: [{ text: `Process the following message content and generate the requested pieces:\n\n${input}` }]
    };
  } else if (mode === 'file' && typeof input !== 'string') {
    contents = {
      parts: [
        {
          inlineData: {
            mimeType: input.mimeType,
            data: input.base64
          }
        },
        { text: "Analyze this file (audio, video, or document) and generate the requested shareable content pieces: 5 key points, 5 quotes, and 3 captions." }
      ]
    };
  } else {
    throw new Error("Invalid input for processing");
  }

  const result = await ai.models.generateContent({
    model,
    contents,
    config: {
      systemInstruction,
      responseMimeType: "application/json",
      responseSchema,
    }
  });

  const text = result.text;
  if (!text) throw new Error("No content generated");

  return JSON.parse(text) as GeneratedContent;
};
